"use client";

import React, { useMemo, useState, useEffect } from "react";
import { ArrowUpRight, FolderGit2, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { Post } from "@/integrations/wordpress/types";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/34353530-219b-4449-ada3-2a5cc3f78c33.png-wvc-sizes",
  alt: "ROBOTO Academy mascot"
};
interface ShowcaseConsumerProps {
  programs: Post[];
  programsLoading: boolean;
  programsError: string | null;
  lang?: string;
}
function ShowcaseConsumer({
  programs,
  programsLoading,
  programsError,
  lang: propLang
}: ShowcaseConsumerProps) {
  const [lang, setLang] = useState<string>(() => {
    if (propLang) return propLang;
    if (typeof document !== "undefined" && document.documentElement.lang) {
      return document.documentElement.lang.startsWith("vi") ? "vi" : "en";
    }
    return "en";
  });
  useEffect(() => {
    if (propLang) {
      setLang(propLang);
    }
  }, [propLang]);
  const {
    posts: projects,
    loading: projectsLoading,
    error: projectsError
  } = usePosts();
  const isLoading = programsLoading || projectsLoading;
  const error = programsError || projectsError;

  // Pair each program with matching projects
  const groupedData = useMemo(() => {
    if (!programs || !projects) return [];
    return programs.map(program => {
      const progDomain = (program.customFields?.project_domain_link || (program as any).acf?.project_domain_link || program.title || "").toString().trim().toLowerCase();
      const matchingProjects = projects.filter(project => {
        const projDomain = (project.customFields?.field_domain || (project as any).acf?.field_domain || "").toString().trim().toLowerCase();
        return projDomain.length > 0 && progDomain.length > 0 && (projDomain === progDomain || progDomain.includes(projDomain) || projDomain.includes(progDomain));
      });
      return {
        program,
        projects: matchingProjects.slice(0, 3)
      };
    }).filter(group => group.projects.length > 0);
  }, [programs, projects]);
  if (isLoading) {
    return <div className="space-y-12">
        {Array.from({
        length: 2
      }).map((_, i) => <div key={i} data-index={i} className="space-y-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-border pb-4">
              <Skeleton className="h-8 w-60 rounded-md" />
              <Skeleton className="h-4 w-32 rounded-md" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({
            length: 3
          }).map((_, j) => <div key={j} data-index={j} className="rounded-xl border border-border bg-card p-4 space-y-4">
                  <Skeleton className="h-44 w-full rounded-lg" />
                  <Skeleton className="h-5 w-3/4 rounded-md" />
                  <Skeleton className="h-4 w-full rounded-md" />
                </div>)}
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
        <p className="text-sm">Could not load showcase items right now.</p>
      </div>;
  }
  if (groupedData.length === 0) {
    return <div className="rounded-xl border border-border bg-card p-12 text-center">
        <FolderGit2 className="mx-auto size-10 text-muted-foreground" />
        <h3 className="mt-4 font-serif text-xl font-bold text-foreground">
          Student builds in progress
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          New student showcase builds will appear here shortly.
        </p>
      </div>;
  }
  return <div className="space-y-16">
      {groupedData.map((group, pIdx) => {
      const programTitle = group.program.customFields?.program_name || group.program.title || "Academic Program";
      const programLink = group.program.link || "#";
      const programDomain = group.program.customFields?.project_domain_link || (group.program as any).acf?.project_domain_link || "Discipline";
      return <article key={group.program.id} data-index={pIdx} className="rounded-2xl border border-border bg-muted/30 p-6 md:p-8">
            {/* Program Row Header */}
            <div className="flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs uppercase tracking-wide">
                    {programDomain}
                  </Badge>
                  {group.program.customFields?.level && <Badge variant="outline" className="text-xs">
                      {group.program.customFields.level}
                    </Badge>}
                </div>
                <h3 className="mt-2 font-serif text-2xl font-bold tracking-tight text-foreground md:text-3xl">
                  {programTitle}
                </h3>
              </div>

              <Button variant="ghost" asChild className="group w-fit text-foreground hover:text-secondary font-medium px-0 sm:px-3">
                <Link to={programLink}>
                  <span>View program</span>
                  <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Button>
            </div>

            {/* Matching Project Cards Grid */}
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {group.projects.map((project, prjIdx) => {
            const fallbackTitle = lang === "vi" ? "Dự Án Học Viên" : "Student Project";
            const projectName = project.customFields?.project_name || project.title || fallbackTitle;
            const projectDesc = project.customFields?.short_description || project.excerpt || "A hands-on build applying engineering and creative problem-solving concepts.";
            const projectLink = project.link || "#";
            const rawImage = project.customFields?.image || project.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/project_robotics_prototype.png";
            const projectImage = {
              src: typeof rawImage === "string" ? rawImage : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/project_robotics_prototype.png",
              srcset: "",
              sizes: ""
            };
            const domainTag = project.customFields?.field_domain || (project as any).acf?.field_domain || "STEM";
            const ageRange = project.customFields?.age_range;
            return <div key={project.id} data-index={prjIdx} className="h-full min-w-0">
                    <Card className="group flex h-full min-w-0 flex-col justify-between overflow-hidden border border-border bg-card shadow-xs transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-1 hover:border-secondary hover:shadow-md">
                      <div className="min-w-0">
                        {/* Media Box */}
                        <div className="relative overflow-hidden bg-muted">
                          <AspectRatio ratio={16 / 10}>
                            {projectImage.src ? <img src={projectImage.src} data-wvc-srcset={projectImage.srcset} data-wvc-sizes={projectImage.sizes} alt={projectName} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <div className="flex size-full items-center justify-center bg-muted text-muted-foreground">
                                <Cpu className="size-8 opacity-40" />
                              </div>}
                          </AspectRatio>
                          <div className="absolute top-3 left-3 flex gap-1.5">
                            <Badge variant="secondary" className="text-2xs shadow-xs">
                              {domainTag}
                            </Badge>
                            {ageRange && <Badge variant="outline" className="bg-card/90 text-2xs backdrop-blur-xs">
                                {ageRange}
                              </Badge>}
                          </div>
                        </div>

                        {/* Content */}
                        <CardContent className="p-5 min-w-0">
                          <h4 className="font-serif text-lg font-bold text-card-foreground group-hover:text-primary transition-colors">
                            <Link to={projectLink} className="hover:underline">
                              {projectName}
                            </Link>
                          </h4>
                          <p className="mt-2 line-clamp-3 text-sm text-muted-foreground leading-relaxed">
                            {projectDesc}
                          </p>
                        </CardContent>
                      </div>

                      {/* Card Footer Link */}
                      <div className="border-t border-border px-5 py-3">
                        <Link to={projectLink} className="inline-flex items-center text-xs font-semibold text-secondary hover:underline gap-1">
                          <span>Explore project</span>
                          <ArrowUpRight className="size-3.5" />
                        </Link>
                      </div>
                    </Card>
                  </div>;
          })}
            </div>
          </article>;
    })}
    </div>;
}
function ShowcaseLoader({
  lang
}: {
  lang?: string;
}) {
  const {
    posts: programs,
    loading: programsLoading,
    error: programsError
  } = usePosts();
  const projectQueryParams = useMemo(() => ({
    post_type: "project",
    per_page: 20,
    status: ["publish"]
  }), []);
  const projectQuery = useMemo(() => new WP_Query(projectQueryParams), [projectQueryParams]);
  return <PostsProvider wp_query={projectQuery}>
      <ShowcaseConsumer programs={programs} programsLoading={programsLoading} programsError={programsError} lang={lang} />
    </PostsProvider>;
}
export default function Showcase({
  lang
}: {
  lang?: string;
} = {}) {
  const programQueryParams = useMemo(() => ({
    post_type: "program",
    per_page: 20,
    status: ["publish"]
  }), []);
  const programQuery = useMemo(() => new WP_Query(programQueryParams), [programQueryParams]);
  return <section id="programprojectshowcase" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container relative mx-auto px-4 md:px-6">
        {/* Static Floating Decorative Mascot */}
        <div aria-hidden="true" className="pointer-events-none select-none absolute top-0 right-4 md:right-6 lg:right-8 z-0 hidden md:block">
          <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt={DEFAULT_MASCOT.alt} loading="lazy" className="h-24 md:h-28 lg:h-32 w-auto bg-transparent object-contain drop-shadow-sm select-none pointer-events-none" />
        </div>

        {/* Spec-rail Eyebrow and Section Header */}
        <div className="relative z-10 max-w-xl lg:max-w-2xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px w-8 bg-secondary" />
            <div className="size-1.5 bg-secondary" />
            <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
              Curriculum in Action
            </span>
          </div>
          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            From program to real build
          </h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">
            Every learning module culminates in tangible mechanics, working electronics, or autonomous code built by students.
          </p>
        </div>

        {/* Dynamic Nested Query Container */}
        <PostsProvider wp_query={programQuery}>
          <ShowcaseLoader lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}